/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import React, { useState, useRef, useEffect } from "react";
import Grid from "../Grid/Grid";
import Button from "../Button/Button";
import { FiltersProps } from "./IFilter";
import MultiSelectWrapper from "./MultiSelect/MultiSelect";
import SearchWrapper from "./Search/Search";
import "./index.css";

import {
  DateRangePicker,
  createStaticRanges,
  Range,
  RangeKeyDict,
  StaticRange,
} from "react-date-range";

import { enUS } from "date-fns/locale";
import { subDays, subMonths, subYears } from "date-fns";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

// ---------------------------------------------
// Format Label (CaseType -> Case Type)
// ---------------------------------------------
const formatLabel = (key: string) =>
  key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

// ---------------------------------------------
const Filters: React.FC<FiltersProps & { showCustomRanges?: boolean }> = ({
  filters,
  showSearch,
  onFilterChange,
  onClearFilters,
  showCustomRanges = false,
}) => {
  const [dateRanges, setDateRanges] = useState<Record<string, Range | undefined>>({});
  const [openPickers, setOpenPickers] = useState<Record<string, boolean>>({});
  const pickerRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // ---------------------------------------------
  // Init Date Values (leave undefined initially)
  // ---------------------------------------------
  useEffect(() => {
    if (filters) {
      const initial: Record<string, Range | undefined> = {};
      Object.keys(filters).forEach((key) => {
        const filter = filters[key];
        if (filter?.type === "date") {
          // Ensure it's a valid object and has at least a key for react-date-range
          const val = filter.value;
          if (val && typeof val === "object") {
            initial[key] = { ...val, key: val.key || "selection" };
          } else {
            initial[key] = val;
          }
        }
      });
      setDateRanges(initial);
    }
  }, [filters]);

  // ---------------------------------------------
  // Close Picker on Outside Click
  // ---------------------------------------------
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      Object.keys(pickerRefs.current).forEach((key) => {
        const ref = pickerRefs.current[key];
        if (ref && !ref.contains(event.target as Node)) {
          setOpenPickers((prev) => ({ ...prev, [key]: false }));
        }
      });
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // ---------------------------------------------
  // Custom Static Ranges
  // ---------------------------------------------
  const customRanges: StaticRange[] = createStaticRanges([
    {
      label: "Last Week",
      range: () => ({
        startDate: subDays(new Date(), 7),
        endDate: new Date(),
      }),
    },
    {
      label: "Last Month",
      range: () => ({
        startDate: subMonths(new Date(), 1),
        endDate: new Date(),
      }),
    },
    {
      label: "Last Year",
      range: () => ({
        startDate: subYears(new Date(), 1),
        endDate: new Date(),
      }),
    },
  ]);

  // ---------------------------------------------
  // Date Change Handler
  // ---------------------------------------------
  const handleDateChange = (filterKey: string, selection: Range) => {
    if (selection?.startDate && selection?.endDate) {
      setDateRanges((prev) => ({ ...prev, [filterKey]: selection }));
      onFilterChange(filterKey, selection);
    }
  };

  // ---------------------------------------------
  // Clear All Filters
  // ---------------------------------------------
  const handleClearFilters = () => {
    if (!filters) return;

    Object.keys(filters).forEach((key) => {
      const filter = filters[key];

      if (filter.type === "text") onFilterChange(key, "");
      if (filter.type === "select") onFilterChange(key, []);
      if (filter.type === "date") {
        setDateRanges((prev) => ({ ...prev, [key]: undefined }));
        onFilterChange(key, undefined);
      }
    });

    if (showSearch) onFilterChange("search", "");
    setOpenPickers({});
    onClearFilters && onClearFilters();
  };

  const hasFilters = showSearch || (filters && Object.keys(filters).length > 0);

  // ---------------------------------------------
  // Render
  // ---------------------------------------------
  return (
    <Grid container spacing={2} sx={{ marginBottom: 2, alignItems: "center" }}>
      {/* SEARCH */}
      {showSearch && (
        <Grid size={{ xs: 12 }}>
          <SearchWrapper
            size="small"
            value={filters?.search?.value ?? ""}
            onSearch={(q) => onFilterChange("search", q)}
            fullWidth
          />
        </Grid>
      )}

      {/* FILTER ROW */}
      <Grid container size={{ xs: 12 }} spacing={2} alignItems="center">
        {filters &&
          Object.keys(filters).map((filterKey) => {
            const filter = filters[filterKey];

            // ---------------------------------------------
            // SELECT FILTER
            // ---------------------------------------------
            if (filter.type === "select") {
              return (
                <Grid key={filterKey} size={{ xs: "auto" }}>
                  <MultiSelectWrapper
                    size="small"
                    variant="filled"
                    label={formatLabel(filterKey)}
                    options={filter.options || []}
                    selectedValues={filter.value || []}
                    onChange={(selected) => onFilterChange(filterKey, selected)}
                  />
                </Grid>
              );
            }

            // ---------------------------------------------
            // DATE FILTER
            // ---------------------------------------------
            if (filter.type === "date") {
              const range = dateRanges[filterKey];

              // fallback range so picker works even if undefined
              const pickerRange: Range = (range && range.startDate && range.endDate)
                ? range
                : {
                  startDate: new Date(),
                  endDate: new Date(),
                  key: "selection",
                };

              return (
                <Grid key={filterKey} size={{ xs: "auto" }} sx={{ position: "relative" }}>
                  <div
                    ref={(el) => (pickerRefs.current[filterKey] = el)}
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    <Button
                      variant="text"
                      onClick={() =>
                        setOpenPickers((prev) => ({ ...prev, [filterKey]: !prev[filterKey] }))
                      }
                      sx={{
                        backgroundColor: "#D1D1D1",
                        color: "#333",
                        borderRadius: "4px",
                        textTransform: "none",
                        px: 2,
                      }}
                    >
                      {range && range.startDate instanceof Date && range.endDate instanceof Date
                        ? `${formatLabel(filterKey)}: ${range.startDate.toLocaleDateString()} - ${range.endDate.toLocaleDateString()}`
                        : `${formatLabel(filterKey)}`}
                    </Button>

                    {openPickers[filterKey] && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          zIndex: 10,
                        }}
                      >
                        <DateRangePicker
                          onChange={(ranges: RangeKeyDict) =>
                            handleDateChange(filterKey, ranges?.selection)
                          }
                          moveRangeOnFirstSelection={false}
                          ranges={[pickerRange]} // always pass at least one range
                          locale={enUS}
                          staticRanges={showCustomRanges ? customRanges : []}
                          inputRanges={[]}
                          showDateDisplay={false}
                          className={showCustomRanges ? "" : "hide-defined-ranges"}
                        />
                      </div>
                    )}
                  </div>
                </Grid>
              );
            }

            return null;
          })}

        {/* CLEAR BUTTON */}
        {hasFilters && (
          <Grid size={{ xs: "auto" }}>
            <Button
              variant="text"
              onClick={handleClearFilters}
              sx={{ fontWeight: "bold", color: "#1976d2" }}
            >
              CLEAR FILTERS
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default Filters;
